import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { NgxSpinnerService } from 'ngx-spinner';
import { Messages } from 'src/app/common/enums';
import { EditOrderItemRequest } from 'src/app/contracts/requests/orders/edit-order-item-request';
import { OrderService } from 'src/app/data/order.service';
import { OrderItemToEditDto } from 'src/app/models/dtos/orders/order-item-to-edit-dto';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { DateTime } from 'luxon';

export interface EditOrderItemModalData {
  orderItemId: number;
}

@Component({
  selector: 'app-edit-order-item-modal',
  templateUrl: './edit-order-item-modal.component.html',
  styleUrl: './edit-order-item-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class EditOrderItemModalComponent implements OnInit {
  private orderService = inject(OrderService);
  private spinner = inject(NgxSpinnerService);
  private snackbarService = inject(SnackBarService);
  private formBuilder = inject(FormBuilder);
  dialogRef = inject<MatDialogRef<EditOrderItemModalComponent>>(MatDialogRef);
  data = inject<EditOrderItemModalData>(MAT_DIALOG_DATA);

  editForm: FormGroup | null = null;
  orderItemDto: OrderItemToEditDto | null = null;
  minStartDate: Date;
  originalEmployeesNumber = 0;


  constructor() {
    this.minStartDate = new Date();
    this.setupForm();
  }

  get startDate() {
    return this.editForm.get('startDate');
  }
  get employeesNumber() {
    return this.editForm.get('employeesNumber');
  }

  ngOnInit(): void {
    this.loadData();
  }

  loadData(): void {
    this.spinner.show();

    this.orderService.getOrderItemToEdit(this.data.orderItemId).subscribe({
      next: (data) => {
        this.orderItemDto = data;
        this.originalEmployeesNumber = data.EmployeesNumber;
        this.minStartDate = new Date(data.StartDate);
        this.minStartDate.setDate(this.minStartDate.getDate());

        this.editForm.patchValue({
          submissionDate: this.formatDate(data.SubmissionDate),
          submitterFullName: data.SubmitterFullName,
          employerName: data.EmployerName,
          employerObjectName: data.EmployerObjectName,
          positionExpectationName: data.PositionExpectationName,
          startDate: new Date(data.StartDate),
          employeesNumber: data.EmployeesNumber,
        });

        this.employeesNumber.setValidators([Validators.required, Validators.min(0), Validators.max(this.originalEmployeesNumber)]);
        this.employeesNumber.updateValueAndValidity();

        this.spinner.hide();
      },
      error: (error: HttpErrorResponse) => {
        this.snackbarService.openErrorSnackBar(error.error);
        this.spinner.hide();
        this.dialogRef.close(false);
      },
    });
  }

  onSave(): void {
    if (!this.editForm.valid) {
      return;
    }

    let req: EditOrderItemRequest = {
      StartDate: this.startDate.value,
      EmployeesNumber: this.employeesNumber.value,
    };

    this.spinner.show();
    this.orderService.editOrderItem(this.data.orderItemId, req).subscribe({
      next: () => {
        this.snackbarService.openSuccessSnackBar(Messages.SuccessfullyEditedOrderItem);
        this.dialogRef.close(true);
      },
      complete: () => this.spinner.hide(),
      error: (error: HttpErrorResponse) => {
        this.snackbarService.openErrorSnackBar(error.error);
        this.spinner.hide();
      },
    });
  }

  onClose(): void {
    this.dialogRef.close(false);
  }

  private setupForm(): void {
    this.editForm = this.formBuilder.group({
      submissionDate: [{ value: '', disabled: true }],
      submitterFullName: [{ value: '', disabled: true }],
      employerName: [{ value: '', disabled: true }],
      employerObjectName: [{ value: '', disabled: true }],
      positionExpectationName: [{ value: '', disabled: true }],
      startDate: ['', [Validators.required, this.minStartDateValidator.bind(this)]],
      employeesNumber: ['', [Validators.required, Validators.min(0)]],
    });
  }

  private minStartDateValidator(control: any) {
    if (!control.value || !this.orderItemDto) return null;

    const selectedDate = new Date(control.value);
    const originalDate = new Date(this.orderItemDto.StartDate);

    if (selectedDate < originalDate) {
      return { minDate: true };
    }

    return null;
  }

  private formatDate(date: Date): string {
    if (!date) return '';
    return DateTime.fromJSDate(new Date(date)).toFormat('yyyy-MM-dd');
  }
}
